import { Component, ChangeDetectionStrategy, ElementRef, input, output, signal, inject } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProjectVersion } from '@app/shared/interfaces';
import { DateFormattingHelper } from '../../shared/helpers/date-formatting.helper';

export interface VersionSelectEvent {
  version: ProjectVersion;
  versionName: string;
}

@Component({
  selector: 'app-version-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'onEscapeKey()',
  },
  templateUrl: './version-dropdown.component.html',
  styleUrl: './version-dropdown.component.scss',
  imports: [MatIconModule, MatButtonModule, MatTooltipModule],
})
export class VersionDropdownComponent {
  private elementRef = inject(ElementRef);

  // Signal inputs
  projectVersions = input<ProjectVersion[]>([]);
  selectedVersionId = input<number | undefined>(undefined);
  currentVersion = input<string>('Head');
  disabled = input<boolean>(false);
  emptyStateMessage = input<string>('No versions available');

  // Signal outputs
  versionSelected = output<VersionSelectEvent>();
  newVersionRequested = output<void>();

  // Internal state
  isOpen = signal<boolean>(false);

  toggleDropdown(): void {
    if (this.disabled()) return;
    this.isOpen.update((open) => !open);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectVersion(version: ProjectVersion | null): void {
    if (!version) return;
    const versionName = this.getVersionDisplay(version);

    this.versionSelected.emit({
      version,
      versionName,
    });

    this.closeDropdown();
  }

  createNewVersion(): void {
    this.newVersionRequested.emit();
    this.closeDropdown();
  }

  getVersionDisplay(version: ProjectVersion): string {
    return `${version.majorVersion}.${version.minorVersion}`;
  }

  isHeadVersion(version: ProjectVersion): boolean {
    return version.majorVersion === 0 && version.minorVersion === 0;
  }

  formatTimestamp(timestamp: string): string {
    const formatted = DateFormattingHelper.formatTimestamp(timestamp);
    return formatted || 'N/A';
  }

  isSelectedVersion(version: ProjectVersion): boolean {
    return version.versionId === this.selectedVersionId();
  }

  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  onEscapeKey(): void {
    this.closeDropdown();
  }

  onKeyDown(
    event: KeyboardEvent,
    action: string,
    version?: ProjectVersion,
  ): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      switch (action) {
        case 'createNewVersion':
          this.createNewVersion();
          break;
        case 'selectVersion':
          if (version) {
            this.selectVersion(version);
          }
          break;
        case 'closeDropdown':
          this.closeDropdown();
          break;
      }
    }
  }
}
